import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/** Merge Tailwind classes safely. */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/** Format a number as currency. Coerces numeric strings from API decimal columns. */
export function formatCurrency(
  amount: number | string | null | undefined,
  currency = 'INR',
  locale?: string,
): string {
  const parsed = typeof amount === 'string' ? Number.parseFloat(amount) : amount
  const value = typeof parsed === 'number' && !Number.isNaN(parsed) ? parsed : 0
  const resolvedLocale =
    locale ?? (currency === 'INR' ? 'en-IN' : currency === 'EUR' ? 'de-DE' : 'en-US')

  return new Intl.NumberFormat(resolvedLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/** Convert a base price using an exchange rate, rounded to 2 decimal places. */
export function convertCurrency(basePrice: number, exchangeRate: number): number {
  return Math.round(basePrice * exchangeRate * 100) / 100
}

/** Truncate a string to a max length with ellipsis. */
export function truncate(str: string, maxLength: number): string {
  if (str.length <= maxLength) return str
  return str.slice(0, maxLength - 1) + '…'
}

/** Generate a slug from a string. */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Format a date string to a human-readable format. */
export function formatDate(dateStr: string, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(dateStr))
}

/** Format a date string to include time. */
export function formatDateTime(dateStr: string, locale = 'en-US'): string {
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(dateStr))
}

/** Get initials from a name. */
export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

/** Debounce a function. */
export function debounce<T extends (...args: unknown[]) => unknown>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), delay)
  }
}
